// Carrito (invitado o usuario logueado), cupón y pedidos, contra el
// backend ecommercejwt. Usa el cliente compartido (src/api/ecommerceClient.js)
// y agrega la identidad correspondiente en cada llamada:
//
// - Invitado: la primera vez que se pega sin token, el backend genera un
//   invitado_token y lo devuelve en la respuesta; se guarda en localStorage
//   y se reenvía en cada llamada siguiente (si no, cada request crearía un
//   carrito nuevo y vacío).
// - Usuario logueado: se manda el JWT guardado por auth.js en vez del
//   invitado_token (misma prioridad que el backend: JWT gana si se mandan
//   los dos). El carrito de invitado y el de usuario son carritos
//   completamente distintos en el backend -- no hay merge automático; eso
//   se resuelve del lado del cliente en AuthContext al loguearse.
import { request, buildImageUrl } from './ecommerceClient';
import { getStoredAuth } from './auth';

const GUEST_TOKEN_STORAGE_KEY = 'miestampita_invitado_token';

const getGuestToken = () => {
  try {
    return localStorage.getItem(GUEST_TOKEN_STORAGE_KEY) || '';
  } catch {
    return '';
  }
};

const setGuestToken = (token) => {
  if (!token) return;
  try {
    localStorage.setItem(GUEST_TOKEN_STORAGE_KEY, token);
  } catch {
    // localStorage puede no estar disponible (ej. modo privado); el
    // carrito simplemente no persiste entre recargas.
  }
};

// JWT si hay sesión iniciada; si no, invitado_token (creando uno nuevo la
// primera vez, vía el que devuelva el backend).
const identityParams = () => {
  const { token } = getStoredAuth();
  if (token) return { token };

  const guestToken = getGuestToken();
  return guestToken ? { invitado_token: guestToken } : {};
};

const cartRequest = async (path, options = {}) => {
  const data = await request(path, { ...options, params: { ...identityParams(), ...options.params } });
  if (data?.invitado_token) setGuestToken(data.invitado_token);
  return data;
};

export const getCarrito = async () => {
  const data = await cartRequest('/carrito');
  return {
    ...data,
    items: (data.items || []).map((item) => ({ ...item, imagen_url: buildImageUrl(item.imagen_url) })),
  };
};

export const addCarritoItem = ({ productoId, cantidad, personalizacion }) =>
  cartRequest('/carrito/items', {
    method: 'POST',
    body: JSON.stringify({ producto_id: productoId, cantidad, personalizacion }),
  });

export const updateCarritoItem = (itemId, cantidad) =>
  cartRequest(`/carrito/items/${itemId}`, {
    method: 'PUT',
    body: JSON.stringify({ cantidad }),
  });

export const removeCarritoItem = (itemId) => cartRequest(`/carrito/items/${itemId}`, { method: 'DELETE' });

export const validarCupon = ({ codigo, subtotal }) =>
  cartRequest('/cupones/validar', {
    method: 'POST',
    body: JSON.stringify({ codigo, subtotal }),
  });

// El backend exige los 7 campos de dirección de invitado sin excepción
// (ninguno puede venir vacío) -- a diferencia de CustomerForm.jsx, donde
// el código postal es opcional porque muchas direcciones uruguayas no lo
// tienen cargado en OpenStreetMap. Se rellena con "S/N" si vino vacío para
// no bloquear la creación del pedido por ese campo.
//
// Un usuario logueado, en cambio, tiene que mandar `direccionId` (una de
// sus direcciones guardadas) -- el backend no acepta dirección inline para
// cuentas con sesión.
export const crearPedido = ({ customerData, direccionId, codigoPromocional }) =>
  cartRequest('/pedidos', {
    method: 'POST',
    body: JSON.stringify({
      ...(direccionId
        ? { direccion_id: direccionId }
        : {
            direccion: {
              nombre_completo: customerData.name,
              direccion: customerData.address,
              departamento: customerData.department,
              ciudad: customerData.city,
              codigo_postal: customerData.postalCode?.trim() || 'S/N',
              telefono: customerData.phone,
              email: customerData.email,
            },
          }),
      ...(codigoPromocional ? { codigo_promocional: codigoPromocional } : {}),
    }),
  });
